<template>
  <div class="PaidBilling">
    <div class="PaidBilling__plans">
      <div class="PaidBilling__header">
        <div class="PaidBilling__header-left">
          <h2 class="PaidBilling__title d-inline-block">Your Subscription</h2>
          <small class="PaidBilling__custom_plan ml-4" v-if="customPlan">
            Custom Added Plan
          </small>
        </div>
        <div class="PaidBilling__header-actions" v-if="!customPlan">
          <v-btn
            v-if="hasPlanSelectionChanged"
            large
            @click="cancelPlanSelection"
            color="n5"
            dark
            :disabled="saving"
            class="PaidBilling__header-btn"
          >
            Cancel
          </v-btn>
          <v-btn
            v-if="hasPlanSelectionChanged"
            large
            @click="changePlan"
            color="y1"
            dark
            :disabled="saving"
            class="PaidBilling__header-btn"
          >
            Change Plan
          </v-btn>
        </div>
      </div>
      <div class="PaidBilling__row mb-4">
        <div
          class="PaidBilling__col"
          :class="{
            _active: selectedPlan == plan.name.toLowerCase(),
            '_last-plan': plan.name === plans[plans.length - 1].name,
          }"
          v-for="plan in plans"
          :key="plan.name"
          @click="changeSelectedPlan(plan)"
        >
          <div class="PaidBilling__plan">
            <div class="PaidBilling__plan-head">
              <h4 class="PaidBilling__plan-title">{{ `${plan.name} Plan` }}</h4>
              <span
                class="PaidBilling__plan-label"
                v-if="currentPlan == plan.name.toLowerCase()"
                >current</span
              >
            </div>
            <div class="PaidBilling__plan-body">
              <h6 class="PaidBilling__plan-cost">
                {{ plan.cost }}
              </h6>
              <p class="PaidBilling__plan-subtext">
                (plus VAT where applicable)
              </p>
              <p class="PaidBilling__plan-text _bold">{{ plan.sitesCount }}</p>
              <p
                class="PaidBilling__plan-text"
                v-for="feature in baseFeatures"
                :key="feature"
              >
                {{ feature }}
              </p>
              <p class="PaidBilling__plan-text">
                <span> {{ plan.bandwidth }} bandwidth </span>/ month
                <br />
                ({{ plan.extra }}¢ per extra GB)
              </p>
            </div>
          </div>
        </div>
        <!-- Team Plan Promotional Widget -->
        <div class="PaidBilling__team-promo">
          <div class="PaidBilling__team-badge">NEW</div>
          <h4 class="PaidBilling__team-title">Iron Plan for Teams</h4>
          <div class="PaidBilling__team-price">
            <span class="PaidBilling__team-price-amount">$249</span>
            <span class="PaidBilling__team-price-period">/month</span>
          </div>
          <p class="PaidBilling__team-tagline">
            One simple, fixed monthly price for your entire team.
          </p>
          <div class="PaidBilling__team-benefits">
            <div class="PaidBilling__team-benefit">
              <v-icon color="white" small class="PaidBilling__team-check">mdi-check</v-icon>
              <span>Unlimited team members</span>
            </div>
            <div class="PaidBilling__team-benefit">
              <v-icon color="white" small class="PaidBilling__team-check">mdi-check</v-icon>
              <span>Team workspace</span>
            </div>
            <div class="PaidBilling__team-benefit">
              <v-icon color="white" small class="PaidBilling__team-check">mdi-check</v-icon>
              <span>Member management</span>
            </div>
            <div class="PaidBilling__team-benefit">
              <v-icon color="white" small class="PaidBilling__team-check">mdi-check</v-icon>
              <span>Unlimited projects</span>
            </div>
            <div class="PaidBilling__team-benefit">
              <v-icon color="white" small class="PaidBilling__team-check">mdi-check</v-icon>
              <span>Centralized billing</span>
            </div>
            <div class="PaidBilling__team-benefit">
              <v-icon color="white" small class="PaidBilling__team-check">mdi-check</v-icon>
              <span>Premium Support</span>
            </div>
          </div>
          <v-btn
            large
            @click="createTeamWorkspace"
            color="y1"
            dark
            class="PaidBilling__team-cta"
          >
            GET STARTED
          </v-btn>
        </div>
      </div>

      <div v-if="errorMain" class="error-main pa-4 mt-2">
        <div v-for="e in errorMain">{{ e }}</div>
      </div>
    </div>
    <div class="PaidBilling__sections">
      <div class="PaidBilling__row _with-gap">
        <div class="PaidBilling__section">
          <h2 class="PaidBilling__title">Billing Details</h2>
          <p class="PaidBilling__section-text">
            {{ billingDetailsText }}
          </p>
          <v-btn large @click="upgradeDetails" color="n5" dark>
            Update Billing Details
          </v-btn>
        </div>
        <div class="PaidBilling__section">
          <h2 class="PaidBilling__title">Coupon</h2>
          <p class="PaidBilling__section-text" v-if="!couponSavePressed">
            Got a coupon code? Enter it here
          </p>
          <p
            class="PaidBilling__section-text"
            v-else-if="couponSavePressed && couponSaved"
          >
            Coupon Saved!
          </p>
          <p class="PaidBilling__section-text red--text" v-if="couponError">
            {{ couponError }}
          </p>
          <v-text-field
            class="PaidBilling__section-input"
            dark
            filled
            v-model="subscriptionCode"
            dense
          />
          <div class="PaidBilling__section-footer">
            <v-btn
              large
              @click="enterCode"
              color="y1"
              :disabled="!subscriptionCodeEntered"
              dark
            >
              Save
            </v-btn>
          </div>
        </div>
      </div>
      <div class="PaidBilling__bottom-actions">
        <v-btn
          @click="cancelSubscription"
          color="n5"
          dark
          :disabled="saving"
          class="PaidBilling__cancel-btn"
        >
          Cancel Subscription
        </v-btn>
        <v-btn
          @click="closeAccount"
          color="n5"
          dark
          class="PaidBilling__close-btn"
        >
          Close Account
        </v-btn>
        <div v-if="cancelErrorsExists" class="error-main pa-4 my-2">
          <div v-for="e in cancelErrors">{{ e }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { MODAL_ALERT, MODAL_CARD_DATA, MODAL_CREATE_ORGANISATION } from '@/components/Modals'
import { STRIPE_PUBLIC_KEY } from '@/env'
export default {
  name: 'PaidBilling',
  props: {
    baseFeatures: Array,
    plans: Array,
    currentPlan: String,
    userId: Number,
    sitesCount: Number,
  },
  data() {
    return {
      selectedPlan: this.currentPlan,
      subscriptionCode: '',
      cancelErrors: '',
      couponError: '',
      couponSaved: null,
      errorMain: '',
      saving: false,
    }
  },
  methods: {
    changeSelectedPlan(plan) {
      if (this.customPlan) return
      this.selectedPlan = plan.name.toLowerCase()
    },
    cancelPlanSelection() {
      this.selectedPlan = this.currentPlan
      this.errorMain = ''
    },
    async confirmStripeAction(secret) {
      // confirmCardPayment automatically handles 3D Secure authentication
      // It will show a modal if needed and wait for user to complete it
      const stripe = Stripe(STRIPE_PUBLIC_KEY)
      try {
        const res = await stripe.confirmCardPayment(secret)
        if (res.error) {
          this.errorMain = res.error.message || 'Payment not confirmed. Please check your card'
          this.saving = false
          return
        }
        if (res.paymentIntent.status !== 'succeeded') {
          this.errorMain = 'Payment not confirmed. Please check your card'
          this.saving = false
          return
        }
        // Payment succeeded, update subscription
        const params = {
          session: {
            plan_id: this.selectedPlan,
          },
        }
        await this.$store.dispatch('user/checkSubscription', params)
        // Reload page to ensure user object is updated with new plan_id
        location.reload()
      } catch (error) {
        this.errorMain = error.message || 'Payment failed. Please try again'
        this.saving = false
      }
    },
    changePlan() {
      if (this.saving) return
      this.saving = true
      this.errorMain = ''
      let params = {
        id: this.$store.state.user.current.id,
        session: {
          plan_id: this.selectedPlan,
        },
      }
      this.$store
        .dispatch('user/updateSubscription', params)
        .then((res) => {
          if (res.client_secret) {
            this.confirmStripeAction(res.client_secret)
            return
          }
          // If no client_secret, subscription was updated successfully
          // Reload page to ensure user object is updated with new plan_id
          location.reload()
        })
        .catch((e) => {
          this.errorMain = e.body.errors_array ? e.body.errors_array.map((e) => e + '.') : (e.body.errors ? Object.values(e.body.errors).flat().map((e) => e + '.') : ['Failed to update subscription'])
          this.saving = false
        })
    },
    upgradeDetails() {
      this.$store.commit('application/openModal', {
        component: MODAL_CARD_DATA,
        props: {
          paid: true,
          baseCountry: this.$store.state.user.current.country,
        },
      })
    },
    cancelSubscription() {
      this.cancelErrors = ''
      this.saving = true
      const user_params = {
        id: this.userId,
        session: {
          stripe_card_token: null,
          plan_id: 'free',
          subscription_active: false,
        },
      }
      this.$store.commit('application/openModal', {
        component: MODAL_ALERT,
        props: {
          title: 'Are you sure you want to cancel your subscription?',
          text: "Your payment method will no longer be billed but you'll still have access to a free Forge account. If you want to cancel an account entirely please contact our team at <a href='mailto:support@getforge.com'>support@getforge.com</a>",
          showCancelBtn: true,
          actionBtnLabel: 'Yes, cancel my subscription',
          action: () =>
            this.$store
              .dispatch('user/updateSubscription', user_params)
              .then(() => {
                this.saving = false
              })
              .catch((e) => {
                this.cancelErrors = e.body.errors.map((e) => e + '.')
                this.saving = false
              }),
        },
      })
    },
    enterCode() {
      this.couponError = ''
      const user_params = {
        id: this.userId,
        session: {
          coupon: this.subscriptionCode,
        },
      }
      this.$store
        .dispatch('user/updateSubscription', user_params)
        .then(() => {
          this.couponSaved = true
          this.subscriptionCode = ''
          const self = this
          setTimeout(() => {
            self.couponSaved = null
          }, 4000)
        })
        .catch((e) => {
          console.log(e)
          if (e.body.errors.coupon != undefined)
            this.couponError = e.body.errors.coupon.join('. ')
          this.couponSaved = null
        })
    },
    closeAccount() {
      // Placeholder for future implementation
      // Will be a soft deletion with backend flagging
      // All projects and sites will be removed
      this.$store.commit('application/openModal', {
        component: MODAL_ALERT,
        props: {
          title: 'Close Account',
          text: 'This feature is coming soon. To close your account, please contact support at <a href="mailto:support@getforge.com">support@getforge.com</a>',
          actionBtnLabel: 'Close',
        },
      })
    },
    createTeamWorkspace() {
      this.$store.commit('application/openModal', {
        component: MODAL_CREATE_ORGANISATION,
      })
    },
  },
  computed: {
    subscriptionCodeEntered() {
      return this.subscriptionCode.length > 0
    },
    customPlan() {
      return this.$store.state.user.current.customPaidPlan
    },
    cancelErrorsExists() {
      return this.cancelErrors.length > 0
    },
    couponSavePressed() {
      return this.couponSaved != null
    },
    hasPlanSelectionChanged() {
      // Show buttons when a different plan is selected than the current one
      return this.selectedPlan !== this.currentPlan && this.selectedPlan !== null
    },
    billingDetailsText() {
      if (this.customPlan)
        return "If you want to change your plan, click below. We will debit this payment method every month. Users with custom added plan can't select their plan."
      else
        return 'If you need to change your card details, click below. We will debit this payment method every month.'
    },
  },
}
</script>

<style lang="scss">
$style: PaidBilling;
.#{$style} {
  padding: 24px 16px 16px;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    flex-wrap: wrap;
    gap: 16px;
  }
  &__header-left {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }
  &__header-actions {
    display: flex;
    gap: 12px;
    align-items: center;
  }
  &__header-btn {
    min-width: auto;
  }
  &__title {
    color: $white;
    font-weight: 500;
    font-size: $H18;
    margin-bottom: 0;
  }
  &__row {
    display: flex;
    flex-wrap: wrap;
    &._with-gap {
      gap: 16px;
      flex-wrap: nowrap;
    }
  }
  &__col {
    flex: 1 1 0;
    max-width: 25%;
    border: 1px solid $N5;
    border-right-color: transparent;
    cursor: pointer;
    &._last-plan {
      border-right-color: $N5;
    }
    &:first-child {
      border-radius: 8px 0 0 8px;
    }
    &._active {
      background-color: $N4;
      border: 1px solid $Y1;
    }
  }
  &__team-promo {
    flex: 1 1 0;
    max-width: 25%;
    background: linear-gradient(135deg, rgba($Y1, 0.15) 0%, rgba($Y1, 0.05) 100%);
    border: 2px solid $Y1;
    border-radius: 8px;
    padding: 24px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    position: relative;
    margin-left: 16px;
  }
  &__team-badge {
    position: absolute;
    top: 12px;
    right: 12px;
    background-color: $Y1;
    color: $white;
    font-size: 10px;
    font-weight: 600;
    padding: 4px 8px;
    border-radius: 4px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  &__team-title {
    color: $white;
    font-weight: 600;
    font-size: $H18;
    margin-bottom: 12px;
    margin-top: 8px;
  }
  &__team-price {
    display: flex;
    align-items: baseline;
    justify-content: center;
    margin-bottom: 8px;
  }
  &__team-price-amount {
    color: $white;
    font-weight: 700;
    font-size: 36px;
    line-height: 1;
  }
  &__team-price-period {
    color: $white;
    font-size: $H14;
    margin-left: 4px;
    opacity: 0.9;
  }
  &__team-tagline {
    color: $N2;
    font-size: $H12;
    margin-bottom: 20px;
    line-height: 1.4;
  }
  &__team-benefits {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    margin-bottom: 24px;
    text-align: left;
  }
  &__team-benefit {
    display: flex;
    align-items: center;
    gap: 8px;
    color: $white;
    font-size: $H12;
    span {
      flex: 1;
    }
  }
  &__team-check {
    background-color: rgba($Y1, 0.2);
    border-radius: 4px;
    padding: 2px;
  }
  &__team-cta {
    width: 100%;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    background: linear-gradient(135deg, $Y1 0%, darken($Y1, 5%) 100%);
    box-shadow: 0 2px 8px rgba($Y1, 0.3);
    &:hover {
      box-shadow: 0 4px 12px rgba($Y1, 0.4);
    }
  }
  &__custom_plan {
    color: $Y1;
    border: 1px solid $Y1;
    border-radius: 8px;
    padding: 10px;
  }
  &__plan {
    display: flex;
    flex-direction: column;
    width: 100%;
    align-items: center;
    padding: 12px;
  }
  &__plan-head {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
  }
  &__plan-title {
    color: $white;
    font-weight: 600;
    font-size: $H18;
    padding: 8px 0;
  }
  &__plan-body {
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  &__plan-cost {
    color: $white;
    font-weight: 600;
    font-size: $H16;
    margin-bottom: 16px;
  }
  &__plan-label {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 8px;
    background-color: $Y1;
    border-radius: 20px;
    position: absolute;
    top: 0;
    left: 50%;
    transform: translate(-50%, -22px);
  }
  &__plan-subtext {
    color: $N2;
    font-size: $H11;
    margin-bottom: 16px;
  }
  &__plan-text {
    color: $white;
    font-weight: 400;
    font-size: $H14;
    margin-bottom: 10px;
    text-align: center;
    &._bold {
      font-weight: 600;
    }
    span {
      font-weight: 600;
    }
  }
  &__plans-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 16px 0;
    gap: 16px;
  }
  &__section {
    @extend %section-styles;
    flex: 1 1 0;
    min-width: 0;
  }
  &__input {
    @extend %input-styles;
  }
  &__section-footer {
    display: flex;
  }
  &__bottom-actions {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    margin-top: 32px;
    padding-top: 24px;
    border-top: 1px solid $N5;
  }
  &__cancel-btn,
  &__close-btn {
    font-size: 14px;
    text-transform: none;
    padding: 8px 16px;
    min-width: auto;
  }
  @media screen and (max-width: 600px) {
    &__col {
      max-width: 50%;
    }
    &__team-promo {
      max-width: 100%;
      margin-left: 0;
      margin-top: 16px;
    }
    &__section {
      flex-basis: 100%;
    }
    &__bottom-actions {
      flex-direction: column;
      gap: 8px;
    }
  }
}
</style>
